"use client"

import React, {useState} from 'react';
import Header2 from "@/app/(index)/header2";
import cx from "clsx";
import {Noto_Sans_TC, Poppins} from 'next/font/google';
import Footer from "@/app/(index)/footer";
import Bread from "@/app/(index)/bread";
import {ActionIcon, Button, Divider, Image, Modal, ModalBody, SimpleGrid} from "@mantine/core";
import useGlobalStateStore from "@/app/(index)/use-global-state-store";
import {CoreResource2} from "@/app/(index)/core-resource";
import {useDisclosure, useMediaQuery} from "@mantine/hooks";
import "./styles.module.css";
import "./styles.css";
import ReadMore, {ReadMoreWhite} from "@/app/(index)/read-more";
import {IconX} from "@tabler/icons-react";


const notoSansTC = Noto_Sans_TC({
    weight: ['400', '500', '700', '800', '900'], // 指定粗細
    subsets: ['latin'],     // 指定支援的字符集
    display: 'swap',        // 字型載入行為
});


const poppins = Poppins({
    weight: ['400', '500', '700'], // 指定粗細
    subsets: ['latin'],     // 指定支援的字符集
    display: 'swap',        // 字型載入行為
});


const summaryEnForm: {[key: string]: string } = {
    GRB: 'Government Research Bulletin (GRB) is Taiwan’s most comprehensive platform for publicly funded research information. GRB collects research projects and reports funded by various government agencies. It features a searchable database covering project titles, budgets, and keywords, and allows researchers to register and manage their projects. \n' +
        'GRB also offers thematic services aligned with national priorities, presenting related studies in structured formats. With rich content from agencies like the NSTC and a user-friendly web interface, GRB supports policymakers, academics, and industries in accessing and utilizing government-funded research. It enhances transparency, promotes knowledge sharing, and serves as a key reference for Taiwan’s science and technology policy.',
    PRIDE: 'Policy Research Indicators Database (PRIDE) is a numeric database to support S&T policy research. Since its launch in 2014, PRIDE has integrated statistical indicators from sources such as Eurostat, OECD, WEF, World Bank, and domestic agencies, offering a one-stop platform for researchers. It covers data on population, economy, education, environment, labor, government performance, quality of life, and science and technology. \n' +
        'PRIDE provides key features like indicator search, chart generation and a personalized charting area for user-generated data. \n' +
        'By visualizing complex data, PRIDE helps researchers identify trends, correlations, and generate new research ideas.',
    TOAJ: 'Taiwan Open Access Journals (TOAJ) platform was launched by the National Science and Technology Council (NSTC) to increase the visibility and accessibility of Taiwan’s humanities and social sciences research. While many local journals offer free full-text access, they lack a unified platform. TOAJ fills this gap by collecting peer-reviewed journal articles and supporting local editorial teams in editing and dissemination. It offers free, open access to scholarly content, promotes research sharing and reuse, and strengthens Taiwan’s international academic presence. As a practical open access initiative, TOAJ fosters collaboration among editors, scholars, and institutions.',
    iKnow: 'iKnow has been operated since 2003. It integrates “market + strategy + patents” to provide the latest industry trends and expert analysis. Covering industrial policy, market developments, patent intelligence, and strategic insights. Key services include a free weekly e-newsletter (issued every Friday), expert consulting on patent, technology, and market information, and licensed article use by media (fees apply per case). It aims to be a key knowledge support platform for policy-making and innovation in Taiwan’s science and technology sectors.',
    OUTLOOK: 'OUTLOOK is an information platform developed to help the National Science and Technology Council (NSTC), policy researchers, and R&D personnel stay updated on global S&T  policy trends and research developments. It collects and processes data from key international sources—government websites, organizations, media, and journals—and categorizes it into four main themes: Policy Trends, Smart Technologies, Biomedical Health, and Energy Technology. Since its public launch in August 2015, OUTLOOK has provided daily updates. Registered users can access all content, subscribe to monthly reports by topic, and manage personal settings.',
    FITI: 'The From IP to IPO (FITI) Program is an entrepreneurship incubation initiative led by the National Science and Technology Council (NSTC) and implemented by STPI. It aims to encourage university students to transform their innovative ideas into viable startup projects. The program provides funding support, entrepreneurial training, mentorship from industry experts, and opportunities for venture capital matchmaking. FITI helps young innovators take their first steps into entrepreneurship and cultivates future startups and talent.',
    'HUZU Platform': 'HUZU Platform serves as a commercialization service platform for academic research outcomes. The platform provides online tools and matchmaking services to help university faculty and students connect their research and innovations with industry through collaborations, technology transfer, and startup creation, facilitating commercialization. HUZU offers three main services that aim to support the successful commercialization of academic research and innovation: entrepreneurial tools, intellectual property certification tools, and a matchmaking platform.'
}

const linkEnForm: {[key: string]: string } = {
    GRB: 'https://www.grb.gov.tw/',
    PRIDE: 'https://pride.stpi.niar.org.tw/',
    TOAJ: 'https://toaj.stpi.niar.org.tw/',
    iKnow: 'https://iknow.stpi.niar.org.tw/',
    OUTLOOK: 'https://outlook.stpi.niar.org.tw/',
    FITI: 'https://fiti.stpi.niar.org.tw/',
    'HUZU Platform': 'https://huzu.stpi.niar.org.tw/',
}

const Page = ({ params }: { params: { team_id: string, id: string } }) => {
    const { fontSize, lang } = useGlobalStateStore();
    const mediaQueryLessSM = useMediaQuery('(min-width: 480px)');
    const [opened, {open, close}] = useDisclosure(false);
    const [linkTag, setLinkTag] = useState<string>('GRB');


    const showEnLinkInfo = (tag: string) => {
        console.log(tag);


        setLinkTag(tag);
        open();
    }

    return (
        <div className={cx("min-h-screen flex flex-col justify-between", notoSansTC.className)}>
            <div>
                <Header2/>
                {
                    mediaQueryLessSM && (
                        <Bread name_c="七大核心服務平台" name_e="Core Resources"/>
                    )
                }

                {/*Source-7-BG-M.jpg*/}
                <div className={cx("mx-auto mb-[100px]", mediaQueryLessSM ? 'w-[1250px]' : 'w-full mt-[100px] px-[10px]')}>
                    <div className="bg-[url('/images/title_topBG.png')] bg-[length:100%_5px] bg-no-repeat bg-top inline-block pt-[15px] pb-[45px] text-[#5b5b5b] text-[35px] font-bold">
                        { lang == "tw" ? "七大核心服務平台" : "Core Resources"}
                    </div>

                    <div className="text-[20px] leading-[40px]">
                        {
                            lang == "tw" ? (
                                <>
                                    <div>本核心服務平台整合七個平台，依功能劃分為三大類別：</div>
                                    <span className="text-[#4f938f] font-bold">資料庫與出版平台</span>（政府研究資訊系統、政策研究指標資料庫、臺灣學術期刊開放取用平台）、
                                    <span className="text-[#157698] font-bold">科技趨勢編彙</span>（科技產業資訊室、科技發展觀測平台），以及
                                    <span className="text-[#35a94a] font-bold">創新創業推動</span>（創新創業激勵計畫、新型態產學互助創新平台）。三大類別協同運作，提供政策研究、產業洞察與創新創業全方位支持。
                                </>
                            ) : (
                                <>
                                    <div>Our core resources integrate seven branded services, organized into three categories:</div>
                                    <div>
                                        <span className="text-[#4f938f] font-bold">Databases and Publishing Platforms</span> – GRB, PRIDE, TOAJ
                                    </div>
                                    <div>
                                        <span className="text-[#157698] font-bold">Science & Technology Trend</span> – iKnow, OUTLOOK
                                    </div>
                                    <div>
                                        <span className="text-[#35a94a] font-bold">Innovation and Entrepreneurship Promotion</span> – FITI, HUZU
                                    </div>
                                    <div>
                                        These core resources are aligned to offer integrated support for policy research, industry analysis, and innovation and entrepreneurship development.
                                    </div>
                                </>
                            )
                        }
                    </div>
                    <Divider color={"#cedbdc"} mt={35} mb={30}/>
                    {
                        mediaQueryLessSM ? (
                            <div className={"flex gap-[35px] items-center"}>
                                <div>
                                    { lang == "tw" ? <Image src={`/images/Source-7-BG.jpg`} /> : <Image src={`/images/Source-7-BG-EN.jpg`} /> }
                                </div>
                                <div className="flex flex-col flex-1">
                                    <div className="border-[#c9e2e1] border-b pt-[25px] pb-[30px]">
                                        <SimpleGrid cols={3}>
                                            <CoreResource2 name={lang == "tw" ? "政府研究資訊系統" : "Government Research Bulletin (GRB)"} image="team_LOGO-GRB" url="www.grb.gov.tw" handleEnLinkInfo={showEnLinkInfo} tag={"GRB"}/>
                                            <CoreResource2 name={lang == "tw" ? "政策研究指標資料庫" : "Policy Research Indicators Database (PRIDE)" } image="team_LOGO-PRIDE" url="pride.stpi.niar.org.tw" handleEnLinkInfo={showEnLinkInfo} tag={"PRIDE"}/>
                                            <CoreResource2 name={lang == "tw" ? "臺灣學術期刊開放取用平台" : "Taiwan Open Access Journals (TOAJ)"} image="team_LOGO-TOAJ" url="toaj.stpi.niar.org.tw" handleEnLinkInfo={showEnLinkInfo} tag={"TOAJ"}/>
                                        </SimpleGrid>
                                    </div>
                                    <div className="border-[#c9e2e1] border-b pt-[25px] pb-[30px]">
                                        <SimpleGrid cols={3}>
                                            <CoreResource2 name={lang == "tw" ? "科技產業資訊室" : "iKnow"} image="team_LOGO-IKNOW" url="iknow.stpi.niar.org.tw" handleEnLinkInfo={showEnLinkInfo} tag={"iKnow"}/>
                                            <CoreResource2 name={lang == "tw" ? "科技發展觀測平台" : "Science, Technology and Innovation Policy Outlook (OUTLOOK)"} image="team_LOGO-OUTLOOK" url="outlook.stpi.niar.org.tw" handleEnLinkInfo={showEnLinkInfo} tag={"OUTLOOK"}/>
                                        </SimpleGrid>
                                    </div>
                                    <div className="pt-[25px] pb-[30px]">
                                        <SimpleGrid cols={3}>
                                            <CoreResource2 name={lang == "tw" ? "創新創業激勵計畫" : "From IP to IPO Program (FITI)"} image="team_LOGO-PM" url="fiti.stpi.niar.org.tw" handleEnLinkInfo={showEnLinkInfo} tag={"FITI"}/>
                                            <CoreResource2 name={lang == "tw" ? "新型態產學互助創新平台" : "Innovation Ecosystem Service Platform (HUZU Platform)"} image="team_LOGO-HUZU" url="huzu.stpi.niar.org.tw" handleEnLinkInfo={showEnLinkInfo} tag={"HUZU Platform"}/>
                                        </SimpleGrid>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className={"flex flex-col gap-[35px]"}>
                                <div>

                                    { lang == "tw" ? <Image src={`/images/Source-7-BG-M.jpg`} /> : <Image src={`/images/Source-7-BG-EN-M.jpg`} /> }
                                </div>

                                <SimpleGrid cols={3}>
                                    <div className="flex flex-col gap-3">
                                        <div className="h-[120px]">
                                            <CoreResource2 name={lang == "tw" ? "政府研究資訊系統" : "Government Research Bulletin (GRB)"} image="team_LOGO-GRB" url="www.grb.gov.tw" handleEnLinkInfo={showEnLinkInfo} tag={"GRB"}/>
                                        </div>
                                        <div className="h-[140px]">
                                            <CoreResource2 name={lang == "tw" ? "政策研究指標資料庫" : "Policy Research Indicators Database (PRIDE)" } image="team_LOGO-PRIDE" url="pride.stpi.niar.org.tw" handleEnLinkInfo={showEnLinkInfo} tag={"PRIDE"}/>
                                        </div>
                                        <CoreResource2 name={lang == "tw" ? "臺灣學術期刊開放取用平台" : "Taiwan Open Access Journals (TOAJ)"} image="team_LOGO-TOAJ" url="toaj.stpi.niar.org.tw" handleEnLinkInfo={showEnLinkInfo} tag={"TOAJ"}/>
                                    </div>
                                    <div className="flex flex-col gap-3">
                                        <div className="h-[120px]">
                                            <CoreResource2 name={lang == "tw" ? "科技產業資訊室" : "iKnow"} image="team_LOGO-IKNOW" url="iknow.stpi.niar.org.tw" handleEnLinkInfo={showEnLinkInfo} tag={"iKnow"}/>
                                        </div>
                                        <CoreResource2 name={lang == "tw" ? "科技發展觀測平台" : "Science, Technology and Innovation Policy Outlook (OUTLOOK)"} image="team_LOGO-OUTLOOK" url="outlook.stpi.niar.org.tw" handleEnLinkInfo={showEnLinkInfo} tag={"OUTLOOK"}/>
                                    </div>
                                    <div className="flex flex-col gap-3">
                                        <div className="h-[120px]">
                                            <CoreResource2 name={lang == "tw" ? "創新創業激勵計畫" : "From IP to IPO Program (FITI)"} image="team_LOGO-PM" url="fiti.stpi.niar.org.tw" handleEnLinkInfo={showEnLinkInfo} tag={"FITI"}/>
                                        </div>
                                        <CoreResource2 name={lang == "tw" ? "新型態產學互助創新平台" : "HUZU Platform"} image="team_LOGO-HUZU" url="huzu.stpi.niar.org.tw" handleEnLinkInfo={showEnLinkInfo} tag={"HUZU"}/>
                                    </div>
                                </SimpleGrid>
                            </div>
                        )
                    }
                </div>
            </div>
            <Footer/>
            <Modal opened={opened}
                   onClose={close}
                   withCloseButton={false}
                   size={"80%"}
                   centered
            >
                <ModalBody>
                    <div className="flex flex-col gap-5 text-white">
                        <div className="text-[30px] font-bold flex justify-between">
                            <div>{linkTag}</div>
                            <div>
                                <ActionIcon variant="filled" onClick={close} color={"#76a3a1"} radius={"xl"} size={"xl"}>
                                    <IconX style={{ width: '70%', height: '70%' }} stroke={3} />
                                </ActionIcon>
                            </div>
                        </div>
                        <div className="text-[18px] text-justify font-semibold">
                            {summaryEnForm[linkTag]}
                        </div>
                        <div>
                            <ReadMoreWhite url={linkEnForm[linkTag]} />
                        </div>
                    </div>
                </ModalBody>
            </Modal>
        </div>
    );
}

export default Page
